import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Printer } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MaskLines } from "@/components/site/Reveal";
import { HousingAdmin } from "@/components/site/Housing";
import { SessionsAdmin } from "@/components/site/SessionsAdmin";
import { CertificateCell } from "@/components/site/Certificate";
import { SchoolRunsAdmin } from "@/components/site/SchoolRunsAdmin";

const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "");

const Table = ({ cols, rows, testid }) => (
  <div className="overflow-x-auto rounded-2xl bg-white/60" data-testid={testid}>
    <table className="w-full min-w-[640px] text-left text-sm">
      <thead><tr className="border-b border-ink/15">{cols.map((c) => <th key={c} className="eyebrow px-5 py-4 text-ink/70">{c}</th>)}</tr></thead>
      <tbody>
        {rows.length ? rows : <tr><td colSpan={cols.length} className="px-5 py-10 text-ink/60">Nothing here yet.</td></tr>}
      </tbody>
    </table>
  </div>
);

const Td = ({ children }) => <td className="px-5 py-4 align-top">{children}</td>;

export default function Admin() {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/overview").then((r) => setData(r.data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status, completed_on = "") => {
    try { await api.patch(`/admin/enrolments/${id}`, { status, completed_on }); toast.success(`Enrolment ${status}`); load(); } catch (e) { toast.error(formatError(e)); }
  };

  const setSupport = async (id, status) => {
    try { await api.patch(`/admin/support/${id}`, { status }); toast.success(`Marked ${status}`); load(); } catch (e) { toast.error(formatError(e)); }
  };

  const setRole = async (id, role) => {
    try { await api.patch(`/admin/users/${id}/role`, { role }); toast.success(`Role set to ${role}`); load(); } catch (e) { toast.error(formatError(e)); }
  };

  const setTransport = async (id, transport_status) => {
    try { await api.patch(`/admin/enrolments/${id}/transport`, { transport_status }); toast.success("Transport updated"); load(); } catch (e) { toast.error(formatError(e)); }
  };

  const d = { enrolments: [], enquiries: [], pledges: [], users: [], support: [], donations: [], sessions: [], totals: {}, ...(data || {}) };
  const rides = d.enrolments.filter((e) => e.needs_transport);
  const stats = [["Enrolments", d.enrolments.length], ["Support", d.support.length], ["Lifts needed", d.totals?.transport ?? 0], ["Donated", `$${(d.totals?.donated ?? 0).toLocaleString()}`]];

  return (
    <main className="min-h-screen bg-sand px-5 pb-24 pt-36 sm:px-10">
      <div className="mx-auto max-w-[1440px]">
        <p className="eyebrow text-ochre">Admin</p>
        <MaskLines lines={["Overview."]} className="mt-4 font-display text-5xl font-medium tracking-tight sm:text-7xl" />
        <Link to="/admin/run-sheet" data-testid="admin-runsheet-link" className="btn-line mt-6 border-ink/30 text-ink hover:bg-ink hover:text-sand"><Printer className="h-4 w-4" /> Daily run sheet</Link>
        <Link to="/driver" data-testid="admin-driver-link" className="btn-line ml-3 mt-6 border-ink/30 text-ink hover:bg-ink hover:text-sand">Driver view</Link>
        <div className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-4">
          {stats.map(([k, v]) => (
            <div key={k} data-testid={`admin-stat-${k.toLowerCase().replace(/\s+/g, "-")}`} className="rounded-2xl bg-ink p-6 text-sand">
              <p className="eyebrow text-amber">{k}</p><p className="mt-3 font-display text-5xl">{v}</p>
            </div>
          ))}
        </div>
        <HousingAdmin />
        <Tabs defaultValue="enrolments" className="mt-12">
          <TabsList className="h-auto flex-wrap rounded-full bg-ink/5 p-1">
            {["enrolments", "sessions", "transport", "school runs", "support", "donations", "enquiries", "pledges", "users"].map((t) => (
              <TabsTrigger key={t} value={t} data-testid={`admin-tab-${t.replace(/\s+/g, "-")}`} className="rounded-full px-5 py-2 capitalize data-[state=active]:bg-ink data-[state=active]:text-sand">{t}</TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="sessions" className="mt-6">
            <SessionsAdmin sessions={d.sessions} reload={load} />
          </TabsContent>
          <TabsContent value="school runs" className="mt-6">
            <SchoolRunsAdmin />
          </TabsContent>
          <TabsContent value="transport" className="mt-6">
            <Table testid="admin-transport-table" cols={["Person", "Programme", "Session", "Lift for", "Pick-up suburb", "Notes", "Status"]} rows={rides.map((e) => (
              <tr key={e.id} className="border-b border-ink/10">
                <Td><p className="font-semibold">{e.user_name}</p><p className="text-xs text-ink/65">{e.user_email}</p></Td>
                <Td>{e.course_title}</Td><Td>{e.session_label || "—"}</Td><Td>{e.transport_for}</Td><Td>{e.pickup_suburb}</Td><Td>{e.transport_notes || "—"}</Td>
                <Td>
                  <select data-testid={`admin-transport-status-${e.id}`} value={e.transport_status || "requested"} onChange={(ev) => setTransport(e.id, ev.target.value)} className="rounded-full border border-ink/20 bg-transparent px-3 py-1.5 text-xs">
                    {[["requested", "Requested"], ["arranged", "Arranged"], ["not_possible", "Not possible"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </Td>
              </tr>
            ))} />
          </TabsContent>
          <TabsContent value="enrolments" className="mt-6">
            <Table testid="admin-enrolments-table" cols={["Student", "Course", "Note", "Date", "Status", "Certificate"]} rows={d.enrolments.map((e) => (
              <tr key={e.id} className="border-b border-ink/10">
                <Td><p className="font-semibold">{e.user_name}</p><p className="text-xs text-ink/65">{e.user_email}</p></Td>
                <Td>{e.course_title}{e.needs_transport && <span className="ml-2 rounded-full bg-ochre/15 px-2 py-0.5 text-[11px] text-ochre-dark">Lift</span>}</Td><Td>{e.note || "—"}</Td><Td>{fmt(e.created_at)}</Td>
                <Td>
                  <select data-testid={`admin-status-${e.id}`} value={e.status} onChange={(ev) => setStatus(e.id, ev.target.value)} className="rounded-full border border-ink/20 bg-transparent px-3 py-1.5 text-xs">
                    {["pending", "approved", "declined", "completed"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {e.status === "completed" && (
                    <input type="date" data-testid={`admin-completed-on-${e.id}`} value={e.completed_on || ""} onChange={(ev) => ev.target.value && setStatus(e.id, "completed", ev.target.value)}
                      title="Completion date (used for certificate renewal reminders)" className="mt-2 block rounded-full border border-ink/20 bg-transparent px-3 py-1 text-xs" />
                  )}
                </Td>
                <Td><CertificateCell e={e} reload={load} /></Td>
              </tr>
            ))} />
          </TabsContent>
          <TabsContent value="support" className="mt-6">
            <Table testid="admin-support-table" cols={["Person", "Service", "Contact", "Safe to contact", "Message", "Date", "Status"]} rows={d.support.map((s) => (
              <tr key={s.id} className="border-b border-ink/10">
                <Td>{s.name || "Anonymous"}</Td><Td><span className="rounded-full bg-gum/15 px-3 py-1 text-xs">{s.service}</span></Td><Td><p className="font-semibold">{s.contact}</p><p className="text-xs text-ink/65">{s.contact_method}</p></Td>
                <Td>
                  {s.safe_times?.length > 0 && <p className="text-xs font-semibold">{s.safe_times.join(", ")}</p>}
                  <p>{s.safe_to_contact || (s.safe_times?.length ? "" : "—")}</p>
                  {s.code_word && <p data-testid={`admin-code-word-${s.id}`} className="mt-1 text-xs">Code word: <span className="rounded bg-ochre/15 px-1.5 py-0.5 font-mono text-ochre-dark">{s.code_word}</span></p>}
                  {s.ok_to_leave_message && <p className="mt-1 text-xs text-ink/70">Voicemail OK: <strong>{s.ok_to_leave_message}</strong></p>}
                </Td><Td><p className="max-w-md whitespace-pre-wrap">{s.message}</p></Td><Td>{fmt(s.created_at)}</Td>
                <Td>
                  <select data-testid={`admin-support-status-${s.id}`} value={s.status} onChange={(ev) => setSupport(s.id, ev.target.value)} className="rounded-full border border-ink/20 bg-transparent px-3 py-1.5 text-xs">
                    {["new", "contacted", "closed"].map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </Td>
              </tr>
            ))} />
          </TabsContent>
          <TabsContent value="enquiries" className="mt-6">
            <Table testid="admin-enquiries-table" cols={["From", "Topic", "Message", "Date"]} rows={d.enquiries.map((e) => (
              <tr key={e.id} className="border-b border-ink/10">
                <Td><p className="font-semibold">{e.name}</p><p className="text-xs text-ink/65">{e.email} {e.phone}</p></Td>
                <Td>{e.topic}</Td><Td><p className="max-w-md whitespace-pre-wrap">{e.message}</p></Td><Td>{fmt(e.created_at)}</Td>
              </tr>
            ))} />
          </TabsContent>
          <TabsContent value="donations" className="mt-6">
            <Table testid="admin-donations-table" cols={["Donor", "Amount", "Frequency", "Message", "Date", "Payment"]} rows={d.donations.map((p) => (
              <tr key={p.id} className="border-b border-ink/10">
                <Td><p className="font-semibold">{p.name}</p><p className="text-xs text-ink/65">{p.email}</p></Td>
                <Td>${p.amount}</Td><Td>{p.frequency}</Td><Td>{p.message || "—"}</Td><Td>{fmt(p.created_at)}</Td>
                <Td><span className={`rounded-full px-3 py-1 font-mono text-xs uppercase ${p.payment_status === "paid" ? "bg-gum text-sand" : "bg-ink/10"}`}>{p.payment_status}</span></Td>
              </tr>
            ))} />
          </TabsContent>
          <TabsContent value="pledges" className="mt-6">
            <Table testid="admin-pledges-table" cols={["Donor", "Amount", "Frequency", "Message", "Date"]} rows={d.pledges.map((p) => (
              <tr key={p.id} className="border-b border-ink/10">
                <Td><p className="font-semibold">{p.name}</p><p className="text-xs text-ink/65">{p.email}</p></Td>
                <Td>${p.amount}</Td><Td>{p.frequency}</Td><Td>{p.message || "—"}</Td><Td>{fmt(p.created_at)}</Td>
              </tr>
            ))} />
          </TabsContent>
          <TabsContent value="users" className="mt-6">
            <Table testid="admin-users-table" cols={["Name", "Email", "Role", "Joined"]} rows={d.users.map((u) => (
              <tr key={u.id} className="border-b border-ink/10"><Td>{u.name}</Td><Td>{u.email}</Td><Td>
                {u.role === "admin" ? "admin" : (
                  <select data-testid={`admin-role-${u.id}`} value={u.role} onChange={(ev) => setRole(u.id, ev.target.value)} className="rounded-full border border-ink/20 bg-transparent px-3 py-1.5 text-xs">
                    <option value="student">student</option><option value="driver">driver</option>
                  </select>
                )}
              </Td><Td>{fmt(u.created_at)}</Td></tr>
            ))} />
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
}
